import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {ArrowLeft} from '@mui/icons-material';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {useAccountContext, useAccountFull} from '../../contexts/AccountContext';
import {SelectOption} from '../AutoForm/AutoFieldNew';
import Footer from '../Footer/Footer';
import Loading from '../Loading/Loading';
import {PrecursorFormProgress} from '../Wizard/QuestionWrapper';
import {ErrorPage} from '../../pages/ErrorPage';
import useTitle from '../../hooks/useTitle';
import {usePrePrecursorGQL} from './usePrePrecursorGQL';
import {
  onCreateReviewFactory,
  reviewTypeValuesToLabels,
  reviewTypes,
} from './PrePrecursorHelper';
import {useSetIsJourneyToTrue} from '../../hooks/useSetIsJourneyToTrue';
import theme from '../../theme/Theme';

const classes = {
  questionText: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
  },
};

export const PrePrecursorForm: React.FC = () => {
  const {accountReviewsData} = usePrePrecursorGQL();
  if (!accountReviewsData?.accountDetails) {
    return <Loading />;
  }
  return <PrePrecursorFormInner />;
};

export const PrePrecursorFormInner = () => {
  const {accountReviewsData, refetchAccountReviews, createReview, updateReview} =
    usePrePrecursorGQL();
  const {account, accountId} = useAccountContext();
  const {accountFull: accountDetailed} = useAccountFull();
  useTitle(`Create meeting - ${account?.name}`);
  const previousReviews = accountReviewsData?.accountDetails?.reviews.length;
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {setIsJourneyToTrue} = useSetIsJourneyToTrue();
  const [reviewReason, setReviewReason] = useState<reviewTypes | undefined>(() => {
    const reviewsLength = accountReviewsData?.accountDetails?.reviews.length;
    const defaultReason: reviewTypes = reviewsLength
      ? 'Ongoing client service'
      : 'Initial client meeting';
    return defaultReason;
  });
  const [previousReviewSelected, setPreviousReviewSelected] = useState<
    string | 'new review' | null
  >(null);
  const [loading, setLoading] = useState(false);
  const onCreateReview = onCreateReviewFactory({
    setLoading,
    reviewReason,
    enqueueSnackbar,
    navigate,
    accountId,
    createReview,
    updateReview,
    previousReviewSelected,
    setIsJourneyToTrue,
    refetchAccount: refetchAccountReviews,
  });

  const accountReviews = accountReviewsData?.accountDetails;
  if (!accountReviews || !accountDetailed) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  const options: SelectOption[] = previousReviews
    ? [
        {
          label: 'Annual',
          value: 'Ongoing client service',
        },
        {
          label: 'Ad hoc',
          value: 'Change of client circumstances',
        },
      ]
    : [
        {
          label: 'Initial',
          value: 'Initial client meeting',
        },
      ];
  const draftReviews = accountReviews.draftReviews ? accountReviews.draftReviews : [];
  const adhocDraftReviews = draftReviews.filter(
    (dr) => dr?.reviewReason === 'Change of client circumstances'
  );

  const showConfirmButton =
    reviewReason &&
    (reviewReason !== 'Change of client circumstances' ||
      (reviewReason === 'Change of client circumstances' &&
        (previousReviewSelected || adhocDraftReviews.length === 0)));

  // ON-1472 - Ensure user is aware that no-contact accounts are an anomaly
  if (
    !accountDetailed.isTrust &&
    !accountDetailed?.primaryContact &&
    !accountDetailed?.secondaryContact
  ) {
    return <ErrorPage code="500" text="Account should have at least one contact" />;
  }

  return (
    <>
      <div
        style={{
          display: 'inline-block',
          zIndex: -40,
          lineHeight: '0',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          width: '33% !important',
          minWidth: '380px',
          position: 'absolute',
          left: '60%',
          top: '0%',
        }}
      />
      <Typography
        style={{display: 'flex', marginBottom: 10, alignItems: 'center', gap: 5}}
        variant="body2"
      >
        <Link
          style={{
            color: 'inherit',
            display: 'flex',
            alignItems: 'center',
          }}
          to={{
            pathname: `/account/${accountId}/${ACCOUNT_TAB.REVIEWS}`,
          }}
        >
          <ArrowLeft />
          {account?.name}
        </Link>
      </Typography>
      <div
        style={{
          marginTop: '10%',
          overflow: 'visible',
          left: '0px',
          right: '0px',
          margin: '0px auto',
          position: 'relative',
        }}
      >
        <div>
          <Grid container justifyContent="center">
            <form style={{width: '60rem', margin: '0 auto', paddingLeft: 8}}>
              <Grid container justifyContent="center" alignItems="center" spacing={2}>
                <Grid container xs={12}>
                  <Grid container item xs={4}>
                    <Grid item xs={12} style={{marginBottom: '1rem'}}>
                      <Typography
                        variant="h1"
                        color="textPrimary"
                        style={{fontSize: '1.9em'}}
                      >
                        Review Type
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={classes.questionText}>
                        Please select the review type
                      </Typography>
                    </Grid>
                    <Grid item xs={12} style={{marginTop: '1rem'}}>
                      <div className="question">
                        <FormControl fullWidth size="medium">
                          <TextField
                            select
                            id="reviewtypepreprecursor"
                            InputLabelProps={{
                              style: {
                                marginTop: -2,
                              },
                            }}
                            variant="outlined"
                            label="Review Type"
                            value={reviewReason || null}
                            onChange={(e) =>
                              setReviewReason(e.target.value as reviewTypes)
                            }
                            SelectProps={{
                              MenuProps: {
                                classes: {
                                  root: 'reviewtype', // used for testing
                                },
                              },
                            }}
                          >
                            {options.map((option) => (
                              <MenuItem
                                selected={option.value === reviewReason}
                                key={option.value}
                                value={option.value}
                                data-testid={option.label}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </FormControl>
                      </div>
                      <Box mt={3} />
                      {reviewReason === 'Change of client circumstances' &&
                      adhocDraftReviews.length !== 0 ? (
                        <FormControl fullWidth size="medium">
                          <InputLabel style={{top: -2}} htmlFor="reviewType">
                            Do you want to use an existing review?
                          </InputLabel>
                          <Select
                            variant="outlined"
                            label="Do you want to use an existing review?"
                            value={previousReviewSelected}
                            defaultValue={
                              adhocDraftReviews.length === 0 ? 'new review' : undefined
                            }
                            onChange={(e) =>
                              setPreviousReviewSelected(e.target.value as string)
                            }
                          >
                            <MenuItem key="new" value="new review">
                              Create new review
                            </MenuItem>
                            {adhocDraftReviews.map((draftReview) => {
                              if (!draftReview) {
                                return null;
                              }
                              return (
                                <MenuItem key={draftReview.id} value={draftReview.id}>
                                  {draftReview.account?.name}
                                  {' - '}
                                  {draftReview.reviewReason
                                    ? `${
                                        reviewTypeValuesToLabels[
                                          draftReview.reviewReason as keyof typeof reviewTypeValuesToLabels
                                        ]
                                      } review`
                                    : ''}
                                  {draftReview.dateBooked
                                    ? ` - Booked ${moment
                                        .utc(draftReview.dateBooked)
                                        .format('DD/MM/YYYY')}`
                                    : ''}{' '}
                                  {!draftReview.dateBooked && draftReview.dateScheduled
                                    ? ` - Scheduled ${moment
                                        .utc(draftReview.dateScheduled)
                                        .format('DD/MM/YYYY')}`
                                    : ''}{' '}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      ) : null}
                      {showConfirmButton && (
                        <Box mt={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => {
                              const ignoreLimit =
                                process.env.NODE_ENV === 'development' && e.shiftKey;
                              onCreateReview(false, ignoreLimit);
                            }}
                          >
                            {loading ? 'Loading...' : 'Confirm'}
                          </Button>
                        </Box>
                      )}
                      {showConfirmButton && (
                        <Box mt={3} style={{opacity: 0}}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => {
                              const ignoreLimit =
                                process.env.NODE_ENV === 'development' && e.shiftKey;
                              if (e.altKey) {
                                onCreateReview(true, ignoreLimit);
                              }
                            }}
                          >
                            {loading ? 'Loading...' : 'Skip precursor'}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
          <Footer
            maxWidth={false}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              padding: '10px 26px 26px 26px',
              position: 'fixed',
              bottom: 0,
              left: 220,
              width: 'auto',
              right: 0,
              pointerEvents: 'none',
            }}
          >
            <PrecursorFormProgress previousStep={() => {}} progress={0} disablePrevious />
          </Footer>
        </div>
      </div>
    </>
  );
};
